import { useState } from 'react'

import { Card, CardContent } from '@/app/common/components/Card'
import { parseRequestError } from '@/app/common/utilities'
import { FamilyApplicationQueryKeys } from '@/queries/family_applications/queries'
import {
  ApplicationStatus,
  FamilyApplication,
} from '@/queries/family_applications/requests'
import { useToast } from '@/shad/ui/toast/use-toast'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useSelector } from 'react-redux'
import { PURSE_KEYS } from '../../../../../../queries/program_purses/keys.ts'
import { Purse } from '../../../schema'
import { StepIndicator } from './_components/step-indicator'
import ConfirmAwardStep from './confirm-award-step/confirm-award-step'
import SelectPurseStep from './select-purse-step/select-purse-step'
import { SelectStudentsStepForm } from './select-students-step/select-students-step.form'
import { StudentAwardPreview } from './schema'
import PageLoader from '@/app/common/components/PageLoader'
import { useGetStudentAwardPreview } from './hooks/useGetStudentAwardPreview.tsx'

interface AwardDialogProps {
  onOpenChange: (open: boolean) => void
  selectedStudents: ManualAward
  onAwardChange: (students: ManualAward[number]) => void
  onAwardRemove: (studentId: number) => void
  purses: Purse[]
  onAwardComplete: (purseId: number, studentIds: number[]) => void
  familyApplications: FamilyApplication[]
}

type BaseAwardStudentPayload = { id: number; is_override_award?: false }
type OverrideAwardStudentPayload = {
  id: number
  is_override_award: true
  override_award_amount: number
  override_award_reason: string
}
type AwardStudentPayload = BaseAwardStudentPayload | OverrideAwardStudentPayload

// Award students API function
const awardStudentsToScholarship = async (
  programId: number,
  purseId: number,
  students: AwardStudentPayload[]
) => {
  const response = await axios.post(
    `/programs/${programId}/purses/${purseId}/award_students`,
    { students }
  )
  return response.data
}

const buildAwardStudentsPayload = (
  students: StudentAwardInformation[]
): AwardStudentPayload[] => {
  return students
    .filter(
      (student): student is StudentAwardInformation & { id: number } =>
        student.id != null
    )
    .map((student) =>
      student.isManuallyAwarded
        ? {
            id: student.id,
            is_override_award: true,
            override_award_amount: student.amount_to_be_awarded,
            override_award_reason:
              student.manual_awarded_amount_reason?.trim() ?? '',
          }
        : { id: student.id }
    )
}

export type AwardFormSteps =
  | 'select_purse'
  | 'review_students'
  | 'student_recieveing_additional_award_warning'
  | 'award-student-manually'
  | 'confirm_award'

export type ManualAward = {
  studentId: number
  manualAwardAmount: number
  manualAwardReason: string
}[]

export type StudentAwardInformation = {
  id: number | null
  first_name: string | null
  last_name: string | null
  amount_to_be_awarded: number
  isManuallyAwarded: boolean
  is_recieving_additional_award: boolean
  student_grade: string | null | undefined
  applciant_email: string | null
  status: ApplicationStatus
  total_awarded_amount: number
  manual_awarded_amount_reason: string | null
  base_award_amount: number
  modifier_total: number
}

export function AwardDialog({
  onOpenChange,
  selectedStudents,
  purses,
  onAwardComplete,
  familyApplications,
  onAwardChange,
  onAwardRemove,
}: AwardDialogProps) {
  const [currentStep, setCurrentStep] = useState<AwardFormSteps>('select_purse')
  const [selectedPurseId, setSelectedPurseId] = useState('')
  const [confirmationText, setConfirmationText] = useState('')

  const programId = useSelector(selectSelectedProgram).id
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const selectedPurse = purses.find((p) => p.id.toString() === selectedPurseId)

  const studentIds = selectedStudents
    .map((student) => student.studentId)
    .filter((id): id is number => id != null)

  const shouldFetchPreview =
    currentStep !== 'select_purse' &&
    Boolean(selectedPurse) &&
    studentIds.length > 0

  const awardPreviewQuery = useGetStudentAwardPreview({
    programId,
    purseId: selectedPurse?.id ?? null,
    studentIds,
    enabled: shouldFetchPreview,
  })

  function getStudentInformation(): StudentAwardInformation[] {
    const previewByStudentId = new Map<number, StudentAwardPreview>()
    awardPreviewQuery.data?.student_awards.forEach((preview) => {
      previewByStudentId.set(preview.student_id, preview)
    })
    return selectedStudents.map((student) => {
      const studentInfo = familyApplications.find(
        (app) => app.student_id === student.studentId
      )

      if (!studentInfo) {
        throw new Error('Student not found')
      }

      const preview =
        studentInfo.student_id != null
          ? previewByStudentId.get(studentInfo.student_id)
          : undefined

      const baseAwardAmount = preview?.base_amount ?? 0

      const modifierTotal = preview?.modifier_amount ?? 0

      const automaticAwardAmount = preview?.total_amount ?? 0

      const isManuallyAwarded = student.manualAwardAmount > 0

      const amountToBeAwarded = isManuallyAwarded
        ? student.manualAwardAmount
        : automaticAwardAmount

      const isRecievingAdditionalAward = studentInfo.status === 'Funded'

      return {
        id: studentInfo.student_id,
        first_name: studentInfo.student_first_name,
        amount_to_be_awarded: amountToBeAwarded,
        isManuallyAwarded: isManuallyAwarded,
        last_name: studentInfo.student_last_name,
        student_grade: studentInfo.student_grade,
        status: studentInfo.status,
        is_recieving_additional_award: isRecievingAdditionalAward,
        applciant_email: studentInfo.applicant_email,
        total_awarded_amount: studentInfo.total_awarded_amount || 0,
        manual_awarded_amount_reason: student.manualAwardReason,
        base_award_amount: baseAwardAmount,
        modifier_total: modifierTotal,
      }
    })
  }

  const studentInformation = getStudentInformation()

  // Award students mutation
  const awardStudentsMutation = useMutation({
    mutationFn: async ({
      programId,
      purseId,
      students,
    }: {
      programId: number
      purseId: number
      students: AwardStudentPayload[]
    }) => {
      return await awardStudentsToScholarship(programId, purseId, students)
    },
    onSuccess: () => {
      toast({
        variant: 'default',
        title: 'Scholarships Awarded Successfully',
        description: `Successfully awarded scholarships to ${selectedStudents.length} student${selectedStudents.length !== 1 ? 's' : ''} from the "${selectedPurse?.name}" purse.`,
      })

      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({
        queryKey: PURSE_KEYS.PURSES(programId),
      })

      if (selectedPurse?.id) {
        queryClient.invalidateQueries({
          queryKey: PURSE_KEYS.PURSE_DETAILS(
            String(selectedPurse.id),
            programId
          ),
        })
      }

      // Invalidate family applications pagination query to refresh student list
      queryClient.invalidateQueries({
        queryKey: FamilyApplicationQueryKeys.list_by_program(programId),
      })

      // Call the parent callback
      if (selectedPurse) {
        onAwardComplete(selectedPurse.id, studentIds)
      }

      // Close dialog and reset all state
      onOpenChange(false)
      resetForm()
    },
    onError: (error) => {
      const parsedError = parseRequestError(error)
      toast({
        variant: 'destructive',
        title: 'Award Failed',
        description:
          parsedError.message ||
          'There was an error awarding the scholarships. Please try again.',
      })
    },
  })

  const handlePurseSelect = (purseId: string) => {
    setSelectedPurseId(purseId)
  }

  const handleConfirmAward = () => {
    if (!selectedPurse) return
    const studentsPayload = buildAwardStudentsPayload(studentInformation)
    awardStudentsMutation.mutate({
      programId,
      purseId: selectedPurse.id,
      students: studentsPayload,
    })
  }

  const resetForm = () => {
    setCurrentStep('select_purse')
    setSelectedPurseId('')
    setConfirmationText('')
  }

  const isConfirmationValid =
    confirmationText.trim() === selectedPurse?.name?.trim()
  const getStepContent = () => {
    if (currentStep === 'select_purse') {
      return (
        <SelectPurseStep
          onCancelClick={() => {
            onOpenChange(false)
          }}
          onNext={() => {
            setCurrentStep('review_students')
          }}
          purses={purses}
          selectedPurseId={selectedPurseId}
          onPurseSelect={handlePurseSelect}
        />
      )
    }

    if (!selectedPurse) {
      throw new Error('Selected purse not found')
    }

    const studentSteps: AwardFormSteps[] = [
      'review_students',
      'student_recieveing_additional_award_warning',
      'award-student-manually',
    ]

    if (studentSteps.includes(currentStep)) {
      const selectStudentsStepProps = {
        onManualAwardRemove: (studentId: number) => {
          onAwardRemove(studentId)
        },
        onStepChange: setCurrentStep,
        onCancelClick: () => {
          onOpenChange(false)
        },
        currentStep,
        onManualAwardChange: (values: ManualAward[number]) => {
          onAwardChange({
            studentId: values.studentId,
            manualAwardAmount: values.manualAwardAmount,
            manualAwardReason: values.manualAwardReason,
          })
        },
        purse: selectedPurse,
        selectedStudents: studentInformation,
        isAwardPreviewFetching:
          awardPreviewQuery.isFetching && shouldFetchPreview,
        onNextClick: () => setCurrentStep('confirm_award'),
        onBackClick: () => setCurrentStep('select_purse'),
      }

      if (!shouldFetchPreview) {
        return <SelectStudentsStepForm {...selectStudentsStepProps} />
      }

      return (
        <PageLoader query={awardPreviewQuery}>
          {() => <SelectStudentsStepForm {...selectStudentsStepProps} />}
        </PageLoader>
      )
    }

    return (
      <ConfirmAwardStep
        onBackClick={() => {
          setCurrentStep('review_students')
        }}
        selectedPurse={selectedPurse}
        selectedStudents={studentInformation}
        confirmationText={confirmationText}
        onConfirmationTextChange={setConfirmationText}
        isConfirmationValid={isConfirmationValid}
        onConfirmAward={handleConfirmAward}
        isSubmitting={awardStudentsMutation.isPending}
      />
    )
  }

  return (
    <>
      <Card className="max-w-2xl mx-auto">
        <CardContent className="p-0">
          <div className="bg-gray-50 border-b py-4 rounded-t-lg border-gray-200">
            <StepIndicator currentStep={currentStep} />
          </div>
          <div className="py-4 px-4">{getStepContent()}</div>
        </CardContent>
      </Card>
    </>
  )
}
